import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import { client } from '../../../../apollo'
import { CREATE_BASIC_SETUP } from '../../../../graphql/enterprisePlan'

type FormState = {
  numberOfLicenses: number | null
  orgName: string
  planExpirationDate: string
  planStartDate: string
  stripePromoCode: string
  domains: string[]
}

export default function EnterpriseBasicSetupForm() {
  const [formData, setFormData] = useState<FormState>({
    numberOfLicenses: null,
    orgName: '',
    planExpirationDate: '',
    planStartDate: '',
    stripePromoCode: '',
    domains: [],
  })

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [stripeCouponId, setStripeCouponId] = useState<string | null>(null)

  const [createBasicSetup, { loading }] = useMutation(CREATE_BASIC_SETUP, {
    onCompleted: (data) => {
      const result = data.adminCreateBasicSetupEnterprisePlan
      if (result.statusCode === 200) {
        setSuccess(true)
        setStripeCouponId(result.stripeCouponId)
      } else {
        setError(result.error || 'An unknown error occurred')
      }
    },
    onError: (err) => {
      setError(err.message || 'Failed to create enterprise plan')
    },
    client,
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    let parsedValue
    if (name === 'numberOfLicenses') {
      parsedValue = value ? parseInt(value, 10) : null
    }

    setFormData({
      ...formData,
      [name]: parsedValue || value,
    })
  }

  const handleDomainChange = (index: number, value: string) => {
    const updatedDomains = [...formData.domains]
    updatedDomains[index] = value
    setFormData({
      ...formData,
      domains: updatedDomains,
    })
  }

  const addDomainField = () => {
    setFormData({
      ...formData,
      domains: [...formData.domains, ''],
    })
  }

  const removeDomainField = (index: number) => {
    const updatedDomains = [...formData.domains]
    updatedDomains.splice(index, 1)
    setFormData({
      ...formData,
      domains: updatedDomains.length > 0 ? updatedDomains : [''],
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    // Filter out empty domains
    const filteredDomains = formData.domains.filter(
      (domain) => domain.trim() !== '',
    )

    if (filteredDomains.length === 0) {
      setError('At least one domain is required')
      return
    }

    try {
      await createBasicSetup({
        variables: {
          numberOfLicenses: formData.numberOfLicenses,
          orgName: formData.orgName,
          planExpirationDate: formData.planExpirationDate,
          planStartDate: formData.planStartDate,
          stripePromoCode: formData.stripePromoCode,
          domains: filteredDomains,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }

  return (
    <div className="enterprise-basic-setup-form">
      {success && (
        <div className="success-message">
          Enterprise plan created successfully!
          {stripeCouponId && <p>Stripe Coupon ID: {stripeCouponId}</p>}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="orgName">
            Organization Name
            <input
              type="text"
              id="orgName"
              name="orgName"
              value={formData.orgName}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="numberOfLicenses">
            Number of Licenses (Optional)
            <input
              type="number"
              id="numberOfLicenses"
              name="numberOfLicenses"
              value={formData.numberOfLicenses || ''}
              onChange={handleInputChange}
              min="1"
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="planStartDate">
            Plan Start Date
            <input
              type="date"
              id="planStartDate"
              name="planStartDate"
              value={formData.planStartDate}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="planExpirationDate">
            Plan Expiration Date
            <input
              type="date"
              id="planExpirationDate"
              name="planExpirationDate"
              value={formData.planExpirationDate}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="stripePromoCode">
            Stripe Promo Code
            <input
              type="text"
              id="stripePromoCode"
              name="stripePromoCode"
              value={formData.stripePromoCode}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <div className="domains-section">
            <span id="domains-label">Domains</span>
            {formData.domains.map((domain, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`domain-${index}`} className="domain-input-group">
                <label htmlFor={`domain-${index}`}>
                  <span className="sr-only">Domain {index + 1}</span>
                  <input
                    type="text"
                    id={`domain-${index}`}
                    value={domain}
                    onChange={(e) => handleDomainChange(index, e.target.value)}
                    placeholder="example.com"
                    required={index === 0}
                    aria-labelledby="domains-label"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeDomainField(index)}
                  disabled={formData.domains.length === 1 && index === 0}
                  className="remove-domain-btn"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addDomainField}
              className="add-domain-btn"
            >
              Add Domain
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Creating...' : 'Create Enterprise Plan'}
        </button>
      </form>
    </div>
  )
}
